import { Checkbox, Paper, Stack, Typography } from "~/common/mui";
import { useState } from "react";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { C_SURFACE_BRIGHT, C_WHITE } from "~/common/components/icon/Colors";
import { PriceElements } from "~/domains/pricing/components/Price/PriceV4";
import Spinner from "~/common/components/Spinner/Spinner";
import { Product } from "~/domains/product/Product";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import {
  resolvePackageSizeFromProductData,
  resolveSmallestPackageFromProductData,
} from "~/domains/product/utils/productDescriptorUtils";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

import { useRelatedProducts } from "~/domains/product/redux/relatedProducts/useRelatedProducts";
import { useSelectPricingDetails } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { isNonNull } from "~/common/utils/reducers";
import type { BasicAddToCartItem } from "~/features/addToCart/redux/addToCart/addToCartItemFactory";
import { useTranslation } from "react-i18next";

interface Props {
  productCode: string;
  selectedProducts: ReturnType<typeof useAddServiceProductsDataState>[0];
  onSelectedProductChange: ReturnType<typeof useAddServiceProductsDataState>[1];
}

export const AddServiceProductsSection = ({ productCode, selectedProducts, onSelectedProductChange }: Props) => {
  const { state, entries } = useAddServiceProductsData(productCode);
  const { t } = useTranslation();

  if (state === "error") {
    return null;
  }

  if (state === "loading") {
    return <Spinner />;
  }

  if (entries.length === 0) {
    return null;
  }

  const makeOnSelectProduct =
    ({ product, ...entry }: AddServiceProductEntry) =>
    () => {
      onSelectedProductChange((previous) => {
        if (previous.find((_) => _.productCode === product.base.code)) {
          return previous.filter((_) => _.productCode !== product.base.code);
        } else {
          return [...previous, { productCode: product.base.code, ...entry, isSale: false }];
        }
      });
    };

  return (
    <Paper component={Stack} gap={2} sx={{ backgroundColor: C_SURFACE_BRIGHT, p: 2 }}>
      <Typography variant="titleLarge">{t("page.pdp.addServiceProducts.title")}</Typography>
      <Stack gap={0.5}>
        {entries.map((entry) => (
          <AddServiceProductTile
            key={entry.product.base.code}
            {...entry}
            selected={!!selectedProducts.find((_) => _.productCode === entry.product.base.code)}
            onSelect={makeOnSelectProduct(entry)}
          />
        ))}
      </Stack>
    </Paper>
  );
};

interface AddServiceProductTileProps extends AddServiceProductEntry {
  selected: boolean;
  onSelect: () => void;
}

const AddServiceProductTile = ({
  product,
  selected,
  onSelect,
  packCode,
  priceReference,
  quantity,
}: AddServiceProductTileProps) => {
  const { price } = useSelectPricingDetails({
    productCode: product.base.code,
    priceReference,
    quantity,
    packCode,
    saleActivated: false,
    salesData: undefined,
  });
  return (
    <ProductDescriptorContext value={product}>
      <Paper component={Stack} gap={1} direction={"row"} sx={{ p: 2, alignItems: "center", backgroundColor: C_WHITE }}>
        <Checkbox color="primary" checked={selected} onChange={onSelect} />
        <Product.PdpLink>
          <Product.PrimaryImageV2 size={64} />
        </Product.PdpLink>

        <Stack gap={0.5}>
          <Product.PdpLink>
            <Product.DesignationV2 variant="titleMedium" fontWeight={"bold"} />
          </Product.PdpLink>
          <PriceElements.Price price={price} color="textSecondary" />
        </Stack>
      </Paper>
    </ProductDescriptorContext>
  );
};

export interface AddServiceProductEntry {
  product: ProductDescriptor;
  quantity: number;
  packCode: string;
  priceReference: string;
}

export const useAddServiceProductsDataState = () => {
  const [state, setState] = useState<BasicAddToCartItem[]>([]);

  return [state, setState] as const;
};

const useAddServiceProductsData = (productCode: string) => {
  const { state, relatedProducts } = useRelatedProducts(productCode);
  const productCodes = relatedProducts
    .filter((_) => _.showPostCartModal)
    .slice(0, 2)
    .map((_) => _.code);
  const productDescriptorsMap = ProductService.useMultipleProductDescriptors({
    productCodes,
    priceReference: getInitialPriceReference(),
    options: { fetch: true },
  });
  const entries: AddServiceProductEntry[] = productCodes
    .map((code) => {
      const productDescriptor = productDescriptorsMap[code];
      if (!productDescriptor) {
        return null;
      }
      const packCode = resolveSmallestPackageFromProductData(productDescriptor);
      const packSize = resolvePackageSizeFromProductData(productDescriptor, packCode)!;
      return {
        product: productDescriptor,
        quantity: packSize,
        packCode,
        priceReference: getInitialPriceReference(),
      };
    })
    .filter(isNonNull);

  if (state !== "loaded" || relatedProducts.length === 0) {
    return {
      state,
      entries: [],
    };
  }

  return {
    state,
    entries,
  };
};
